// src/resources/customerRfm/HistoryChart.tsx
//
// "Evolução mensal" (spec §6.2 item 7): barras empilhadas de clientes por
// segmento por mês, a partir de `dashboard_v2_rfm_history`. Com menos de 2
// meses gravados (rollout recente da régua nova), mostra um aviso em vez do
// gráfico — a movimentação de 30 dias do FlowsPanel já cobre a tendência.
import React, { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { __t } from '../../i18n/t';
import { COLORS, cardSx, fmtNum } from '../dashboards/shared';
import { RFM_SEGMENT_ORDER, SEGMENT_META } from './segments';

interface HistoryRow { month: string; segment: string; customers: number; totalValue: number; highValue: number }

const HistoryChart: React.FC<{ history: HistoryRow[] }> = ({ history }) => {
  // Recharts herda a fonte do <body>, não a do tema MUI — mesma receita de
  // PointsEconomySection.tsx pra não destoar do resto do painel.
  const theme = useTheme();
  const chartFont = theme.typography.fontFamily;

  const months = useMemo(() => Array.from(new Set(history.map((h) => h.month))).sort(), [history]);

  if (months.length < 2) {
    const firstMonth = months[0] ?? dayjs().format('YYYY-MM-01');
    return (
      <Box sx={{ ...cardSx }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.ink }}>Evolução mensal</Typography>
        <Typography sx={{ fontSize: 12.5, color: COLORS.muted, mt: 1 }}>
          {__t('O histórico mensal começa a ser guardado em %{month}. A movimentação de 30 dias acima já mostra a tendência.', { month: dayjs(firstMonth).format('MMMM [de] YYYY') })}
        </Typography>
      </Box>
    );
  }

  const data = months.map((month) => {
    const row: Record<string, number | string> = { month: dayjs(month).format('MM/YY') };
    RFM_SEGMENT_ORDER.forEach((segment) => {
      row[segment] = history.find((h) => h.month === month && h.segment === segment)?.customers ?? 0;
    });
    return row;
  });

  return (
    <Box sx={{ ...cardSx }}>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.ink }}>Evolução mensal</Typography>
      <Typography sx={{ fontSize: 12, color: COLORS.muted, mb: 1.5 }}>Clientes por grupo, mês a mês.</Typography>
      <Box sx={{ width: '100%', height: 260, fontFamily: chartFont }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(120,105,223,0.08)" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: COLORS.muted, fontFamily: chartFont }} />
            <YAxis tick={{ fontSize: 11, fill: COLORS.muted, fontFamily: chartFont }} tickFormatter={(v) => fmtNum(v)} />
            {/* i18n: formatter do recharts roda fora de JSX (o plugin de build não
                embrulha função de callback) — __t explícito no rótulo. */}
            <Tooltip
              formatter={(v: any, name: any) => {
                const meta = SEGMENT_META[name as keyof typeof SEGMENT_META];
                return [fmtNum(v), meta ? __t(meta.label) : name];
              }}
              contentStyle={{ fontFamily: chartFont, fontSize: 13, borderRadius: 12, border: 'none', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}
              itemSorter={(item: any) => RFM_SEGMENT_ORDER.indexOf(item.dataKey)}
            />
            {/* recharts 3 ordena a legenda por dataKey (alfabético) se não receber itemSorter —
                ficava "Em risco, Campeões, Hibernando…". Ordem da régua, igual aos cards. */}
            <Legend
              itemSorter={(item: any) => RFM_SEGMENT_ORDER.indexOf(item.dataKey)}
              wrapperStyle={{ fontFamily: chartFont, fontSize: 12 }}
              formatter={(value: string) => {
                const meta = SEGMENT_META[value as keyof typeof SEGMENT_META];
                return meta ? __t(meta.label) : value;
              }}
            />
            {RFM_SEGMENT_ORDER.map((segment) => (
              <Bar key={segment} dataKey={segment} stackId="rfm" fill={SEGMENT_META[segment].color} radius={segment === RFM_SEGMENT_ORDER[RFM_SEGMENT_ORDER.length - 1] ? [4, 4, 0, 0] : undefined} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default HistoryChart;
